package org.example.project.enginemodule

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.files.FileReader
import kotlin.coroutines.resume

object ModuleApi {
    private const val EMPTY_FIELD = "~"
    private const val ENTRY_SEPARATOR = "\u001F"
    private const val VALUE_SEPARATOR = "\u001E"

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun loadSystemModules(): List<All> =
        decodeList(LumaticaApiManager.call("moduleditor/system"))

    suspend fun loadInstalledModuleIds(): Set<Int> =
        decodeIntList(LumaticaApiManager.call("moduleditor/installed-ids")).toSet()

    suspend fun saveSystemModule(module: All): All? {
        val raw = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/moduleditor/system-save",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(
                    buildJsonObject {
                        put("modulId", module.modulId)
                        put("modulName", module.modulName)
                        put("modulBeschreibung", module.modulBeschreibung)
                        put("modulReferenz", module.modulReferenz)
                        put("modulTyp", module.modulTyp)
                        put("modulAktiv", module.modulAktiv)
                        put("moduleGalleryAssetIds", buildJsonArray {
                            module.moduleGalleryAssetIds.filter { it > 0 }.distinct().forEach { add(JsonPrimitive(it)) }
                        })
                        put("modulKategorie", module.modulKategorie)
                        put("zustand", module.zustand)
                        put("modulScreens", boolMap(module.modulScreens))
                        put("updateAvailable", module.updateAvailable)
                    }
                )
            )
        )
            .await()
            .text()
            .await()
        return runCatching { json.decodeFromString<All>(raw) }.getOrNull()
    }

    suspend fun createSystemModule(): All? =
        saveSystemModule(createSysModule(0))

    suspend fun deleteSystemModule(moduleId: Int): Boolean =
        decodeOk(LumaticaApiManager.call("moduleditor/system-delete/${field(moduleId.toString())}"))

    suspend fun loadModuleAssetIds(): List<Int> =
        decodeIntList(LumaticaApiManager.call("moduleditor/assets"))

    suspend fun loadModuleTags(): List<String> =
        decodeStringList(LumaticaApiManager.call("moduleditor/tags"))

    suspend fun saveModuleTag(tag: String): List<String> =
        mutateModuleTag("tag-save", tag)

    suspend fun deleteModuleTag(tag: String): List<String> =
        mutateModuleTag("tag-delete", tag)

    private suspend fun mutateModuleTag(action: String, tag: String): List<String> {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/moduleditor/$action",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(buildJsonObject { put("tag", tag) })
            )
        ).await()
        if (!response.ok) return loadModuleTags()
        val raw = response.text().await()
        return runCatching {
            json.parseToJsonElement(raw).jsonObject["tags"]
                ?.let { json.decodeFromJsonElement<List<String>>(it) }
                .orEmpty()
        }.getOrDefault(emptyList())
    }

    suspend fun uploadModuleAsset(file: File, fileName: String): Int? {
        val dataUrl = readFileAsDataUrl(file) ?: return null
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/moduleditor/asset-upload",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(
                    buildJsonObject {
                        put("fileName", fileName)
                        put("contentType", file.type)
                        put("dataUrl", dataUrl)
                    }
                )
            )
        ).await()
        if (!response.ok) return null
        return decodeAssetId(response.text().await())
    }

    suspend fun deleteModuleAsset(assetId: Int): Boolean =
        decodeOk(LumaticaApiManager.call("moduleditor/asset-delete/${field(assetId.toString())}"))

    suspend fun saveCustomerModuleState(
        moduleId: Int,
        nextState: Int,
        applyLatestModuleData: Boolean,
        markPurchased: Boolean
    ): Map<Int, All> {
        val raw = LumaticaApiManager.call(
            listOf(
                "moduleditor",
                "customer-state",
                EMPTY_FIELD,
                field(moduleId.toString()),
                field(nextState.toString()),
                field(applyLatestModuleData.toString()),
                field(markPurchased.toString())
            ).joinToString("/")
        )
        return decodeList(raw).associateBy { it.modulId }
    }

    private fun decodeList(raw: String): List<All> =
        if (raw.isBlank() || raw == "null") emptyList()
        else runCatching { json.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())

    private fun decodeIntList(raw: String): List<Int> =
        if (raw.isBlank() || raw == "null") emptyList()
        else runCatching { json.decodeFromString<List<Int>>(raw) }.getOrDefault(emptyList())

    private fun decodeStringList(raw: String): List<String> =
        if (raw.isBlank() || raw == "null") emptyList()
        else runCatching { json.decodeFromString<List<String>>(raw) }.getOrDefault(emptyList())

    private fun decodeOk(raw: String): Boolean =
        runCatching { json.parseToJsonElement(raw).jsonObject["ok"]?.jsonPrimitive?.content == "true" }
            .getOrDefault(false)

    private fun decodeAssetId(raw: String): Int? =
        runCatching { json.parseToJsonElement(raw).jsonObject["assetId"]?.jsonPrimitive?.content?.toIntOrNull() }
            .getOrNull()

    private suspend fun readFileAsDataUrl(file: File): String? =
        suspendCancellableCoroutine { continuation ->
            val reader = FileReader()
            reader.onloadend = {
                continuation.resume(reader.result as? String)
                null
            }
            reader.onerror = {
                continuation.resume(null)
                null
            }
            reader.readAsDataURL(file)
        }

    private fun field(value: String): String {
        val clean = value.ifBlank { EMPTY_FIELD }
        return js("encodeURIComponent")(clean) as String
    }

    private fun boolMap(map: Map<String, Boolean>): String =
        field(
            map
                .filterValues { it }
                .entries
                .sortedBy { it.key }
                .joinToString(ENTRY_SEPARATOR) { entry -> "${entry.key}$VALUE_SEPARATOR${entry.value}" }
        )

    private fun textMap(map: Map<String, String>): String =
        field(
            map
                .entries
                .sortedBy { it.key }
                .joinToString(ENTRY_SEPARATOR) { entry -> "${entry.key}$VALUE_SEPARATOR${entry.value}" }
        )
}
